import { Timestamp } from "firebase/firestore";
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Eye,
  Lock,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { useState } from "react";

import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Switch } from "components/ui/switch";
import { Textarea } from "components/ui/textarea";

import {
  instantToWallTime,
  wallTimeToInstant,
} from "../../event-experience/agenda-schedule";
import { FeedbackFormView } from "../../feedback/FeedbackFormView";
import {
  draftPayload,
  draftProblems,
  move,
  newId,
  newQuestion,
  withType,
  type FormDraft,
} from "../../feedback/logic";
import {
  LIMITS,
  QUESTION_TYPES,
  QUESTION_TYPE_LABELS,
  isChoiceType,
  type FeedbackForm,
  type FeedbackQuestion,
  type QuestionType,
} from "../../feedback/schemas";
import { AdminCommandFeedback } from "../AdminCommandFeedback";
import { useAdminCommand } from "../use-admin-command";

export const LOCKED_NOTICE =
  "People have answered this form, so only wording, dates and the open or closed state can change. Questions, types, options and anonymity are fixed.";

export function draftFromForm(
  formId: string,
  form: FeedbackForm | null,
  timeZone: string | undefined,
): FormDraft {
  if (!form) {
    return {
      formId,
      title: "",
      intro: "",
      status: "closed",
      anonymous: false,
      opensAt: "",
      closesAt: "",
      questions: [newQuestion("rating")],
    };
  }
  return {
    formId,
    title: form.title,
    intro: form.intro,
    status: form.status,
    anonymous: form.anonymous,
    opensAt: form.opensAt ? instantToWallTime(form.opensAt, timeZone) : "",
    closesAt: form.closesAt ? instantToWallTime(form.closesAt, timeZone) : "",
    questions: form.questions,
  };
}

/**
 * Create or edit one form. Saving files SAVE_FEEDBACK_FORM; the trigger is
 * what enforces the edit lock, and the controls below only mirror it so an
 * organiser is not invited to make a change that will be refused.
 */
export function FeedbackEditor({
  eventId,
  uid,
  formId,
  form,
  timeZone,
  onBack,
}: {
  eventId: string;
  uid: string;
  formId: string;
  /** The stored form, live, or null while it does not exist yet. */
  form: FeedbackForm | null;
  timeZone?: string;
  onBack: () => void;
}) {
  const { state, submit, reset } = useAdminCommand();
  const [draft, setDraft] = useState<FormDraft>(() =>
    draftFromForm(formId, form, timeZone),
  );
  const [previewing, setPreviewing] = useState(false);
  const [showProblems, setShowProblems] = useState(false);

  const locked = (form?.responseCount ?? 0) > 0;
  const busy = state.status === "submitting" || state.status === "watching";
  const resolve = (wall: string) => wallTimeToInstant(wall, timeZone);
  const problems = draftProblems(draft, resolve);

  const update = (patch: Partial<FormDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));
  const updateQuestion = (index: number, question: FeedbackQuestion) =>
    setDraft((current) => ({
      ...current,
      questions: current.questions.map((existing, i) =>
        i === index ? question : existing,
      ),
    }));

  const save = () => {
    setShowProblems(true);
    if (problems.length > 0 || busy) return;
    void submit(
      "SAVE_FEEDBACK_FORM",
      eventId,
      uid,
      draftPayload(draft, resolve, (date) => Timestamp.fromDate(date)),
    );
  };

  const zoneLabel = timeZone ? `${timeZone} time` : "your device's time";

  return (
    <div className="max-w-3xl min-w-0 space-y-5" data-testid="feedback-editor">
      <Button variant="ghost" className="h-11 px-2" onClick={onBack}>
        <ArrowLeft className="h-4 w-4" />
        All feedback forms
      </Button>

      {locked && (
        <p
          className="flex items-start gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground"
          data-testid="feedback-editor-locked"
        >
          <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0">{LOCKED_NOTICE}</span>
        </p>
      )}
      {locked && (
        <p
          className="flex items-start gap-2 rounded-lg border border-status-warning bg-status-warning/15 px-3 py-2 text-sm text-foreground"
          data-testid="feedback-editor-option-warning"
        >
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0"
            aria-hidden="true"
          />
          <span className="min-w-0">
            Answers are stored against each option, not its wording. Rewording
            an option changes what the answers already given to it mean, so fix
            typos only.
          </span>
        </p>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="feedback-title">Title</Label>
        <Input
          id="feedback-title"
          testId="feedback-title"
          value={draft.title}
          maxLength={LIMITS.title}
          disabled={busy}
          onChange={(event) => update({ title: event.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="feedback-intro">Introduction (optional)</Label>
        <Textarea
          id="feedback-intro"
          data-testid="feedback-intro"
          value={draft.intro}
          maxLength={LIMITS.intro}
          rows={3}
          disabled={busy}
          onChange={(event) => update({ intro: event.target.value })}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <SwitchRow
          id="feedback-open"
          label="Open for answers"
          hint="Attendees see the form on the event page while it is open."
          checked={draft.status === "open"}
          disabled={busy}
          onChange={(checked) =>
            update({ status: checked ? "open" : "closed" })
          }
        />
        <SwitchRow
          id="feedback-anonymous"
          label="Anonymous"
          hint="Answers are stored without names and added to the results in batches every five minutes, so a response only shows which five-minute window it arrived in. If one person answers alone in a window, that window can still point to them. Results show once three people have answered."
          checked={draft.anonymous}
          disabled={busy || locked}
          onChange={(checked) => update({ anonymous: checked })}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="feedback-opens">Opens (optional, {zoneLabel})</Label>
          <Input
            id="feedback-opens"
            testId="feedback-opens"
            type="datetime-local"
            value={draft.opensAt}
            disabled={busy}
            onChange={(event) => update({ opensAt: event.target.value })}
          />
        </div>
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="feedback-closes">
            Closes (optional, {zoneLabel})
          </Label>
          <Input
            id="feedback-closes"
            testId="feedback-closes"
            type="datetime-local"
            value={draft.closesAt}
            disabled={busy}
            onChange={(event) => update({ closesAt: event.target.value })}
          />
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-base font-semibold text-foreground">Questions</h3>
        {draft.questions.length === 0 && (
          <p className="text-sm text-muted-foreground">No questions yet.</p>
        )}
        {draft.questions.map((question, index) => (
          <QuestionEditor
            key={question.id}
            index={index}
            count={draft.questions.length}
            question={question}
            locked={locked}
            disabled={busy}
            onChange={(next) => updateQuestion(index, next)}
            onMove={(delta) =>
              update({ questions: move(draft.questions, index, delta) })
            }
            onRemove={() =>
              update({
                questions: draft.questions.filter((_, i) => i !== index),
              })
            }
          />
        ))}
        <Button
          variant="outline"
          className="h-11"
          disabled={
            busy || locked || draft.questions.length >= LIMITS.questions
          }
          onClick={() =>
            update({ questions: [...draft.questions, newQuestion("rating")] })
          }
          data-testid="feedback-add-question"
        >
          <Plus className="h-4 w-4" />
          Add question
        </Button>
      </div>

      {showProblems && problems.length > 0 && (
        <ul
          role="alert"
          className="list-disc space-y-1 rounded-lg border border-status-error bg-status-error/15 py-2 pl-8 pr-3 text-sm text-foreground"
          data-testid="feedback-editor-problems"
        >
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="cta"
          className="h-11"
          onClick={save}
          disabled={busy}
          data-testid="feedback-save"
        >
          <Save className="h-4 w-4" />
          Save form
        </Button>
        <Button
          variant="outline"
          className="h-11"
          onClick={() => setPreviewing(true)}
          data-testid="feedback-preview"
        >
          <Eye className="h-4 w-4" />
          Preview
        </Button>
      </div>

      <AdminCommandFeedback
        state={state}
        onReset={reset}
        testId="feedback-save"
      />

      <Dialog open={previewing} onOpenChange={setPreviewing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Preview</DialogTitle>
          </DialogHeader>
          <FeedbackFormView
            preview
            form={{
              title: draft.title,
              intro: draft.intro,
              anonymous: draft.anonymous,
              questions: draft.questions,
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SwitchRow({
  id,
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex min-w-0 items-start gap-3 rounded-lg border border-border bg-card p-3 text-card-foreground">
      <Switch
        id={id}
        data-testid={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        className="mt-0.5"
      />
      <div className="min-w-0">
        <Label htmlFor={id}>{label}</Label>
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}

function QuestionEditor({
  index,
  count,
  question,
  locked,
  disabled,
  onChange,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  question: FeedbackQuestion;
  locked: boolean;
  disabled: boolean;
  onChange: (question: FeedbackQuestion) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  const n = index + 1;
  const structural = disabled || locked;
  const options = question.options ?? [];

  return (
    <Card data-testid={`feedback-editor-question-${n}`}>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            Question {n}
          </span>
          <div className="ml-auto flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              aria-label={`Move question ${n} up`}
              disabled={structural || index === 0}
              onClick={() => onMove(-1)}
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              aria-label={`Move question ${n} down`}
              disabled={structural || index === count - 1}
              onClick={() => onMove(1)}
            >
              <ArrowDown className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              aria-label={`Remove question ${n}`}
              disabled={structural}
              onClick={onRemove}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-[12rem_1fr]">
          <div className="min-w-0 space-y-1.5">
            <Label htmlFor={`question-type-${question.id}`}>Type</Label>
            <select
              id={`question-type-${question.id}`}
              data-testid={`question-type-${n}`}
              value={question.type}
              disabled={structural}
              onChange={(event) =>
                onChange(withType(question, event.target.value as QuestionType))
              }
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-base text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
            >
              {QUESTION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {QUESTION_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label htmlFor={`question-label-${question.id}`}>Question</Label>
            <Input
              id={`question-label-${question.id}`}
              testId={`question-label-${n}`}
              value={question.label}
              maxLength={LIMITS.questionLabel}
              disabled={disabled}
              onChange={(event) =>
                onChange({ ...question, label: event.target.value })
              }
            />
          </div>
        </div>

        <label className="flex min-h-11 items-center gap-3 text-sm">
          <Switch
            data-testid={`question-required-${n}`}
            checked={question.required}
            disabled={structural}
            onCheckedChange={(checked) =>
              onChange({ ...question, required: checked })
            }
          />
          Answer required
        </label>

        {isChoiceType(question.type) && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Options</p>
            {options.map((option, optionIndex) => (
              <div key={option.id} className="flex min-w-0 items-center gap-2">
                <Input
                  aria-label={`Question ${n}, option ${optionIndex + 1}`}
                  testId={`question-${n}-option-${optionIndex + 1}`}
                  value={option.label}
                  maxLength={LIMITS.optionLabel}
                  disabled={disabled}
                  onChange={(event) =>
                    onChange({
                      ...question,
                      options: options.map((existing, i) =>
                        i === optionIndex
                          ? { ...existing, label: event.target.value }
                          : existing,
                      ),
                    })
                  }
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 shrink-0"
                  aria-label={`Remove option ${optionIndex + 1}`}
                  disabled={structural || options.length <= LIMITS.minOptions}
                  onClick={() =>
                    onChange({
                      ...question,
                      options: options.filter((_, i) => i !== optionIndex),
                    })
                  }
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              className="h-11"
              disabled={structural || options.length >= LIMITS.maxOptions}
              onClick={() =>
                onChange({
                  ...question,
                  options: [...options, { id: newId("o"), label: "" }],
                })
              }
              data-testid={`question-${n}-add-option`}
            >
              <Plus className="h-4 w-4" />
              Add option
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
